/**
 * Roles del sistema (documento funcional Mirasoles SRL, tabla §7 "Nivel | Rol").
 * Varios roles distintos pueden compartir el mismo nivel de permisos — el nivel 2
 * agrupa 3 roles (Administracion, Compras y Logistica, Supervision) que en el
 * documento aparecen listados juntos porque las 4 personas que hoy los ocupan
 * "tienen el mismo alcance", pero son roles distintos a fines de asignacion y
 * reportes. Es un enum de STRINGS a proposito (no numerico): el valor no debe
 * poder confundirse con el nivel jerarquico. `USER_ROLE_LEVEL` es la unica
 * fuente de verdad para la jerarquia: un usuario de nivel N tiene acceso a
 * todo lo que tiene acceso nivel N+1 (herencia total), asi que la regla de
 * negocio es `USER_ROLE_LEVEL[role] <= USER_ROLE_LEVEL[rolRequerido]`. No
 * existe un campo de "nivel" separado en el usuario — el nivel es siempre
 * derivado del rol.
 */
/**
 * Orden intencional (no alfabético): nivel jerárquico ascendente, y los 3
 * roles de nivel 2 agrupados entre sí. `Object.values(UserRole)` (usado para
 * poblar el <select> de rol en `UserForm.tsx`) sigue el orden de declaración
 * de un enum de string — reordenar acá reordena el dropdown, no hace falta
 * ningún sort aparte en el frontend.
 */
export enum UserRole {
  GERENCIA = 'gerencia',
  ADMINISTRACION = 'administracion',
  COMPRAS_LOGISTICA = 'compras_logistica',
  SUPERVISION = 'supervision',
  OPERADOR_LOGISTICO = 'operador_logistico',
  COCINA = 'cocina',
  /**
   * ISSUE-143: rol externo (RR.HH. de una empresa cliente), NO un nivel más
   * de la jerarquía interna. Se le asigna el nivel más alto de
   * `USER_ROLE_LEVEL` a propósito, para que cualquier `@RequiresRole(...)`
   * interno lo rechace por comparación de nivel sin tener que tocar esas
   * guardas una por una. Ver `openspec/changes/issue-143-portal-cliente-aprobacion-menu/design.md`.
   */
  CLIENTE_RRHH = 'cliente_rrhh',
  /**
   * ISSUE-144: segundo rol externo — el comensal de una empresa cliente.
   * Nivel más alto que `CLIENTE_RRHH` a propósito (mismo mecanismo fail-closed
   * que ese rol): cualquier `@RequiresRole(...)` interno lo rechaza por
   * comparación de nivel. No inicia sesión con email/contraseña — usa
   * `AuthService.loginAsDiner()` (código externo + PIN, ver `UserEntity`).
   */
  COMENSAL = 'comensal',
  /**
   * Tercer rol externo — el proveedor accede al portal de proveedores (`/proveedor`): sus órdenes de
   * compra, su lista de precios y su cuenta. Mismo mecanismo fail-closed que los otros externos (nivel
   * mayor a cualquier rol interno); usa email/contraseña y un `supplierId` propio.
   */
  PROVEEDOR = 'proveedor',
}

export const USER_ROLE_LEVEL: Record<UserRole, number> = {
  [UserRole.GERENCIA]: 1,
  [UserRole.ADMINISTRACION]: 2,
  [UserRole.COMPRAS_LOGISTICA]: 2,
  [UserRole.SUPERVISION]: 2,
  [UserRole.OPERADOR_LOGISTICO]: 3,
  [UserRole.COCINA]: 4,
  [UserRole.CLIENTE_RRHH]: 5,
  [UserRole.COMENSAL]: 6,
  [UserRole.PROVEEDOR]: 7,
};

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.GERENCIA]: 'Gerencia',
  [UserRole.ADMINISTRACION]: 'Administración',
  [UserRole.COMPRAS_LOGISTICA]: 'Compras y Logística',
  [UserRole.SUPERVISION]: 'Supervisión',
  [UserRole.OPERADOR_LOGISTICO]: 'Operador logístico',
  [UserRole.COCINA]: 'Cocina',
  [UserRole.CLIENTE_RRHH]: 'Cliente (RR.HH.)',
  [UserRole.COMENSAL]: 'Comensal',
  [UserRole.PROVEEDOR]: 'Proveedor',
};

/**
 * Marca explícita de "este rol es ajeno a la organización" — NO empleado de
 * Mirasoles. Es la fuente de verdad canónica para esa distinción GENÉRICA
 * (interno vs. externo); el hecho de que `USER_ROLE_LEVEL[CLIENTE_RRHH]` sea
 * el más alto es un efecto buscado para que la comparación de niveles lo
 * excluya de cualquier `@RequiresRole(...)` interno, pero es un detalle de
 * implementación, no la señal canónica — cualquier guard que necesite negar
 * acceso a "cualquier rol externo, sin importar cuál" (ej. `PermissionsGuard`,
 * que protege TODA la superficie interna) debe consultar `isExternalRole()`,
 * no inferirlo comparando niveles.
 *
 * Distinto es un guard de un portal PUNTUAL como `ClientPortalGuard`, que
 * necesita admitir sólo `CLIENTE_RRHH` (no "cualquier externo" — un futuro
 * segundo rol externo, de otro tipo de portal, no debería entrar acá): ese
 * caso compara `role === UserRole.CLIENTE_RRHH` por igualdad exacta a
 * propósito, no vía `isExternalRole()`. Agregar un futuro rol externo (ej.
 * un segundo tipo de usuario de portal) requiere sumarlo acá (para que
 * `PermissionsGuard` lo excluya automáticamente) Y darle su propio guard de
 * portal si necesita uno — sumarlo sólo acá no le abre ningún endpoint.
 */
export const EXTERNAL_ROLES: ReadonlySet<UserRole> = new Set([UserRole.CLIENTE_RRHH, UserRole.COMENSAL, UserRole.PROVEEDOR]);

export function isExternalRole(role: UserRole): boolean {
  return EXTERNAL_ROLES.has(role);
}

export interface IUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  /** Para `role === CLIENTE_RRHH` o `role === COMENSAL`: cliente al que pertenece el usuario. */
  clientId?: string | null;
  /** Sólo para `role === COMENSAL`: código con el que se identifica en el portal (DNI, legajo, etc.), único dentro del cliente. */
  externalCode?: string | null;
  /** Sólo para `role === COMENSAL`: site al que pertenece. */
  siteId?: string | null;
  /** Sólo para `role === PROVEEDOR`: proveedor al que pertenece el usuario. */
  supplierId?: string | null;
  /** Key de storage de la foto de perfil (si tiene); el archivo se pide a `GET /profile/avatar`. */
  avatarKey?: string | null;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface IUserPublic extends Omit<IUser, never> {}
